import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getTrip } from '@/services/tripService'
import { createActivity } from '@/services/activityService'
import {
  geocodeDestination,
  findNearbyActivitySuggestions,
  getWikivoyageHighlight,
  type PoiSuggestion,
} from '@/services/suggestionsService'

const CATEGORY_COLOR: Record<string, string> = {
  transport: '#c97f4a',
  accommodation: '#6e9aa8',
  discovery: '#c9a15a',
  meal: '#9a8fb0',
}

export function SuggestionsPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const destination = tripQuery.data?.destination ?? ''

  const coordinatesQuery = useQuery({
    queryKey: ['suggestions-coordinates', destination],
    queryFn: () => geocodeDestination(destination),
    enabled: !!destination,
  })

  const poisQuery = useQuery({
    queryKey: ['suggestions-pois', coordinatesQuery.data],
    queryFn: () => findNearbyActivitySuggestions(coordinatesQuery.data!),
    enabled: !!coordinatesQuery.data,
  })

  const wikivoyageQuery = useQuery({
    queryKey: ['suggestions-wikivoyage', destination],
    queryFn: () => getWikivoyageHighlight(destination),
    enabled: !!destination,
  })

  const addMutation = useMutation({
    mutationFn: (poi: PoiSuggestion) =>
      createActivity({
        tripId: tripId!,
        title: poi.name,
        category: poi.category,
        location: poi.address ? `${poi.name}, ${poi.address}` : poi.name,
        latitude: poi.latitude,
        longitude: poi.longitude,
      }),
    onSuccess: async (_data, poi) => {
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
      setAddedIds((prev) => new Set(prev).add(poi.id))
    },
  })

  const isLoading = tripQuery.isLoading || coordinatesQuery.isLoading || poisQuery.isFetching
  const trip = tripQuery.data
  const pois = poisQuery.data ?? []
  const wikivoyage = wikivoyageQuery.data

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('suggestions.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('suggestions.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      {wikivoyage && (
        <div className="mt-8 rounded-lg border border-line bg-panel p-6">
          <h2 className="mb-3 font-display text-xl text-ivory">{wikivoyage.title}</h2>
          <p className="text-sm whitespace-pre-line text-mist">{wikivoyage.extract}</p>
          <a
            href={wikivoyage.url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block text-xs text-brass-dim hover:text-brass"
          >
            {t('suggestions.wikivoyageSource')}
          </a>
        </div>
      )}

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        <h2 className="mb-5 font-display text-xl text-ivory">{t('suggestions.nearby')}</h2>

        {isLoading && <p className="text-sm text-mist">{t('suggestions.loading')}</p>}

        {!isLoading && !coordinatesQuery.data && destination && (
          <p className="text-sm text-mist">{t('suggestions.destinationNotFound')}</p>
        )}

        {!isLoading && coordinatesQuery.data && pois.length === 0 && (
          <p className="text-sm text-mist">{t('suggestions.empty')}</p>
        )}

        {pois.map((poi) => (
          <div
            key={poi.id}
            className="mb-2.5 flex items-center justify-between gap-3 rounded-md border border-line bg-panel-2 px-4 py-3"
          >
            <div>
              <div className="flex items-center gap-2 font-sans text-[15px] font-medium text-ivory">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: CATEGORY_COLOR[poi.category] }}
                />
                {poi.name}
              </div>
              <span className="font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                {t(`itinerary.category.${poi.category}`)}
              </span>
            </div>
            {addedIds.has(poi.id) ? (
              <span className="font-sans text-xs text-brass">{t('suggestions.added')}</span>
            ) : (
              <button
                type="button"
                onClick={() => addMutation.mutate(poi)}
                disabled={addMutation.isPending}
                className="shrink-0 font-sans text-xs text-mist hover:text-brass disabled:opacity-50"
              >
                {t('suggestions.addToItinerary')}
              </button>
            )}
          </div>
        ))}

        {addMutation.isError && (
          <p role="alert" className="mt-2 text-sm text-brass-warm">
            {t('suggestions.addError')}
          </p>
        )}
      </div>
    </div>
  )
}
